import React from 'react';
import { Link } from 'react-router-dom';

const Terms: React.FC = () => {
  return (
    <div className='min-h-screen bg-gray-50'>
      <header className='bg-white shadow-sm'>
        <div className='container mx-auto px-4 py-6'>
          <Link
            to='/'
            className='text-2xl font-bold text-primary hover:text-orange-600'
          >
            ← StarJar
          </Link>
        </div>
      </header>

      <main className='container mx-auto px-4 py-12 max-w-4xl'>
        <h1 className='text-4xl font-bold mb-8 text-gray-900'>
          Terms of Service
        </h1>

        <div className='bg-white rounded-lg shadow-md p-8 space-y-6 text-gray-700'>
          <p className='text-sm text-gray-500'>
            Last updated: October 19, 2025
          </p>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              1. Acceptance of Terms
            </h2>
            <p>
              By downloading, installing, or using the StarJar mobile
              application ("the App"), you agree to be bound by these Terms of
              Service ("Terms"). If you do not agree to these Terms, please do
              not use the App.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              2. Description of Service
            </h2>
            <p>
              StarJar is a family behavior tracking and reward management
              application that allows parents and guardians to:
            </p>
            <ul className='list-disc pl-6 space-y-2 mt-2'>
              <li>Create profiles (jars) for family members</li>
              <li>Define positive behaviors to encourage</li>
              <li>Set reward goals and track progress</li>
              <li>Award stars for completed behaviors</li>
              <li>Redeem rewards when goals are achieved</li>
              <li>Sync data across devices via iCloud (optional)</li>
            </ul>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              3. User Accounts and Eligibility
            </h2>
            <p className='mb-2'>
              <strong>Age Requirement:</strong> The App is intended for use by
              parents and guardians aged 18 and older. Children may use the App
              under parental supervision.
            </p>
            <p className='mb-2'>
              <strong>Account Security:</strong> You are responsible for
              maintaining the security of your device and any PIN codes you set
              within the App.
            </p>
            <p>
              <strong>Accurate Information:</strong> You agree to provide
              accurate information when using the App and its features.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              4. Subscription Terms
            </h2>
            <h3 className='text-xl font-semibold mb-2 text-gray-800'>
              Free Tier
            </h3>
            <p>The free version includes:</p>
            <ul className='list-disc pl-6 space-y-1 mt-2 mb-4'>
              <li>1 jar (profile)</li>
              <li>Up to 10 behaviors</li>
              <li>Up to 5 rewards</li>
              <li>Full access to core features</li>
            </ul>

            <h3 className='text-xl font-semibold mb-2 text-gray-800'>
              StarJar Premium
            </h3>
            <p>Premium subscription provides:</p>
            <ul className='list-disc pl-6 space-y-1 mt-2 mb-4'>
              <li>Unlimited jars</li>
              <li>Unlimited behaviors</li>
              <li>Unlimited rewards</li>
              <li>Data export functionality</li>
            </ul>

            <h3 className='text-xl font-semibold mb-2 text-gray-800'>
              Payment and Renewal
            </h3>
            <ul className='list-disc pl-6 space-y-2 mt-2'>
              <li>Subscriptions are processed through Apple's App Store</li>
              <li>
                Payment is charged to your Apple ID account at confirmation of
                purchase
              </li>
              <li>
                Subscriptions automatically renew unless canceled at least 24
                hours before the end of the current period
              </li>
              <li>
                Manage or cancel subscriptions in your App Store account
                settings
              </li>
              <li>No refunds for partial subscription periods</li>
            </ul>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              5. Acceptable Use
            </h2>
            <p>You agree NOT to:</p>
            <ul className='list-disc pl-6 space-y-2 mt-2'>
              <li>Use the App for any illegal or unauthorized purpose</li>
              <li>Attempt to reverse engineer, decompile, or hack the App</li>
              <li>Interfere with or disrupt the App's functionality</li>
              <li>Use the App in any way that could harm minors</li>
              <li>Share your account credentials with others</li>
              <li>Remove or modify any copyright or proprietary notices</li>
            </ul>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              6. Intellectual Property
            </h2>
            <p>
              The App, including its design, code, graphics, and content, is
              owned by StarJar and protected by copyright, trademark, and other
              intellectual property laws. You may not copy, modify, distribute,
              or create derivative works from the App without our express
              written permission.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              7. User Content and Data
            </h2>
            <p className='mb-2'>
              <strong>Your Data:</strong> You retain ownership of all content
              you create within the App (jars, behaviors, rewards,
              transactions).
            </p>
            <p className='mb-2'>
              <strong>Data Storage:</strong> Your data is stored locally on your
              device and optionally in your personal iCloud account. We do not
              access or control your iCloud data.
            </p>
            <p>
              <strong>Data Responsibility:</strong> You are responsible for
              backing up your data. We recommend using iCloud sync or the data
              export feature (Premium).
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              8. Disclaimers and Limitations
            </h2>
            <h3 className='text-xl font-semibold mb-2 text-gray-800'>
              No Parenting Advice
            </h3>
            <p className='mb-4'>
              StarJar is a tool for tracking behaviors and rewards. It does not
              provide parenting, psychological, or medical advice. Consult
              qualified professionals for parenting guidance.
            </p>

            <h3 className='text-xl font-semibold mb-2 text-gray-800'>
              Service Availability
            </h3>
            <p className='mb-4'>
              We strive to maintain reliable service but do not guarantee
              uninterrupted access. The App may be unavailable due to
              maintenance, updates, or circumstances beyond our control.
            </p>

            <h3 className='text-xl font-semibold mb-2 text-gray-800'>
              Limitation of Liability
            </h3>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, STARJAR SHALL NOT BE
              LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
              PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER
              INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, OR
              GOODWILL.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              9. Termination
            </h2>
            <p>
              You may stop using the App at any time by deleting it from your
              device. We reserve the right to suspend or terminate your access
              to the App if you violate these Terms. Upon termination, your
              right to use the App ceases immediately.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              10. Changes to Terms
            </h2>
            <p>
              We may modify these Terms at any time. Continued use of the App
              after changes constitutes acceptance of the modified Terms. We
              will notify users of significant changes through the App or via
              email.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              11. Governing Law
            </h2>
            <p>
              These Terms are governed by the laws of Hong Kong, without regard
              to conflict of law provisions. Any disputes shall be resolved in
              the courts of Hong Kong.
            </p>
          </section>

          <section>
            <h2 className='text-2xl font-bold mb-4 text-gray-900'>
              12. Contact Information
            </h2>
            <p>For questions about these Terms, please contact us at:</p>
            <p className='mt-2'>
              <a
                href='mailto:starjar@twinkle.hk'
                className='text-primary hover:text-orange-600 font-semibold'
              >
                starjar@twinkle.hk
              </a>
            </p>
          </section>

          <section className='border-t pt-6'>
            <p className='text-sm text-gray-600'>
              By using StarJar, you acknowledge that you have read, understood,
              and agree to be bound by these Terms of Service and our Privacy
              Policy.
            </p>
          </section>
        </div>

        <div className='mt-8 text-center'>
          <Link
            to='/'
            className='text-primary hover:text-orange-600 font-semibold'
          >
            ← Back to Home
          </Link>
        </div>
      </main>

      <footer className='bg-gray-900 text-white py-8 mt-12'>
        <div className='container mx-auto px-4 text-center'>
          <p className='text-gray-400'>© 2025 StarJar. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
